import { Box, Heading } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { SourcingEventV2Dto, ViewDocumentAsOwnerDto } from "../../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload, UploadCompletedStatus } from "../../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../../common/documents/UppyUploader/useUppyUploader";
import { StepNavigation } from "../../../../../common/StepNavigation";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  refetch: () => void;
  isActive: boolean;
}

export const DocumentsStep = ({ eventId, event, previousStep, nextStep, nextStepLabel, refetch, isActive }: Props) => {
  const { t } = useTranslation();

  const { uppy, allFiles, documents, setDocuments, removeUpload, reloadEntity } = useUppyUploader({
    entityType: "SourcingEventV2",
    entityId: eventId,
    reloadEntity: refetch,
  });

  useEffect(() => {
    if (isActive) refetch();
  }, [isActive, refetch]);

  useEffect(() => {
    setDocuments(event.eventData.documents ?? []);
  }, [event.eventData.documents, setDocuments]);

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Documents")}
      </Heading>

      <CombinedDocuments
        uploadedDocuments={documents}
        setUploadedDocuments={setDocuments}
        removeUploadedDocument={(document: ViewDocumentAsOwnerDto) =>
          removeUpload({
            status: UploadCompletedStatus.UploadCompleted,
            documentId: document.id,
            uploadId: document.fromUploadId,
            ...document,
          })
        }
        isTagsColumnVisible={false}
        isCategoryColumnVisible
        canEditCategory
        canDeleteDocuments
        canEditDocumentNames
        documentUploadConfig={{
          uppy,
          dropTargetId: `documents-step-${eventId}`,
          dropTargetHelperText: t("Drag and drop files here, or click to browse"),
          uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
          removeUpload,
        }}
        refetchDocuments={() => { reloadEntity(); }}
      />

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
